import { useState } from "react"
import { createFileRoute } from "@tanstack/react-router"
import { useMutation } from "@tanstack/react-query"
import { Layout } from "@/components/layout"
import { BackLink } from "@/components/back-link"
import { Button } from "@/components/ui/button"
import { Textarea } from "@/components/ui/textarea"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { submitContactFeedback } from "@/lib/api/feedback"

export const Route = createFileRoute("/_authenticated/contact")({
  component: Contact,
})

const SUBJECTS = [
  "Missing textbook",
  "Bug report",
  "General feedback",
  "Other",
] as const

function Contact() {
  const [subject, setSubject] = useState<string>(SUBJECTS[0])
  const [text, setText] = useState("")

  const { mutate, isPending, isSuccess } = useMutation({
    mutationFn: () => submitContactFeedback({ subject, text: text.trim() }),
  })

  return (
    // Phone layout: a sub-page of Account (reached from its Support group).
    <Layout back={{ to: "/account", label: "Account" }}>
      <BackLink to="/stories" className="mb-8" />
      <section className="pb-12">
        <h1 className="mb-2 text-4xl font-bold">Contact us</h1>
        <p className="mb-8 text-sm text-muted-foreground">
          Got a question or something to report? We'd love to hear from you.
        </p>

        {isSuccess ? (
          <p className="text-sm text-muted-foreground">
            Thanks for reaching out! We'll get back to you soon.
          </p>
        ) : (
          <form
            className="flex max-w-lg flex-col gap-6"
            onSubmit={(e) => {
              e.preventDefault()
              mutate()
            }}
          >
            <div className="flex flex-col gap-2">
              <label htmlFor="contact-subject" className="text-sm font-medium">
                Subject
              </label>
              <Select
                value={subject}
                onValueChange={(value) => value && setSubject(value)}
              >
                <SelectTrigger
                  id="contact-subject"
                  className="w-full max-w-sm"
                  disabled={isPending}
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {SUBJECTS.map((s) => (
                    <SelectItem key={s} value={s}>
                      {s}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="flex flex-col gap-2">
              <label htmlFor="contact-text" className="text-sm font-medium">
                Message
              </label>
              <Textarea
                id="contact-text"
                value={text}
                onChange={(e) => setText(e.target.value)}
                rows={5}
                placeholder="Tell us what's on your mind…"
                disabled={isPending}
                className="min-h-32"
              />
            </div>

            <div className="flex justify-end">
              <Button type="submit" disabled={isPending || !text.trim()}>
                Send message
              </Button>
            </div>
          </form>
        )}
      </section>
    </Layout>
  )
}
